import React, { useEffect, useMemo, useState } from 'react';
import { useTaps } from '../hooks/useTaps';
import { useBeersMap } from '../hooks/useBeers';
import { useKickTap } from '../hooks/useTapMutations';
import { useTapChangeLog } from '../hooks/useTapChangeLog';
import { useLocation } from '../lib/location';
import { useToast } from '../lib/toast';
import { Icon } from '../components/Icon';
import { LocationPills } from '../components/LocationPills';
import { SwapModalWrapper } from '../components/SwapModal';
import { TapHistoryTable } from '../components/TapHistoryTable';
import type { Beer, KegSize, Tap, TapChangeLogEntry } from '../api/types';

const KEG_SIZE_LABEL: Record<KegSize, string> = {
  half_keg: 'Half-barrel',
  keg:      'Full keg',
  sixtel:   'Sixtel',
  other:    'Other',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hoursAgo(iso: string): string {
  const h = Math.round((Date.now() - new Date(iso).getTime()) / 36e5);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  const days = Math.round(h / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

function formatDayTime(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(today);
  yest.setDate(yest.getDate() - 1);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (d.toDateString() === today.toDateString()) return `Today, ${time}`;
  if (d.toDateString() === yest.toDateString()) return `Yesterday, ${time}`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ', ' + time;
}

type TapStatus = 'on' | 'kicked' | 'empty';

function tapStatus(tap: Tap): TapStatus {
  if (!tap.currentAssignment) {
    return tap.mostRecentKicked ? 'kicked' : 'empty';
  }
  return 'on';
}

// ---------------------------------------------------------------------------
// Status pill
// ---------------------------------------------------------------------------

function StatusPill({ status }: { status: TapStatus }) {
  const styles: Record<TapStatus, React.CSSProperties> = {
    on:     { background: 'transparent', color: 'var(--text)',        borderColor: 'var(--border)' },
    kicked: { background: 'var(--bg-subtle)', color: 'var(--text-muted)', borderColor: 'var(--border)' },
    empty:  { background: 'transparent', color: 'var(--text-subtle)', borderColor: 'var(--border)' },
  };
  const labels: Record<TapStatus, string> = { on: 'ON', kicked: 'KICKED', empty: 'EMPTY' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
      border: '1px solid',
      ...styles[status],
    }}>{labels[status]}</span>
  );
}

// ---------------------------------------------------------------------------
// Tap card
// ---------------------------------------------------------------------------

interface TapCardProps {
  tap: Tap;
  beer: Beer | undefined;
  kickedBeer: Beer | undefined;
  status: TapStatus;
  onOpen: () => void;
  onKick: () => void;
  onSwap: () => void;
}

function TapCard({ tap, beer, kickedBeer, status, onOpen, onKick, onSwap }: TapCardProps) {
  if (status === 'empty') {
    return (
      <div style={{
        background: 'var(--bg-elevated)', border: '1px dashed var(--border)',
        borderRadius: 8, padding: 20,
        display: 'flex', flexDirection: 'column', gap: 8, minHeight: 196,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="uppercase-label">Tap {tap.tapNumber}{tap.label ? ` · ${tap.label}` : ''}</div>
          <StatusPill status="empty" />
        </div>
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--text-subtle)', fontSize: '0.875rem',
        }}>
          No keg assigned
        </div>
        <button
          onClick={onSwap}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontWeight: 700, fontSize: '0.8125rem', padding: '6px 12px',
            borderRadius: 6, background: 'var(--bg-subtle)', color: 'var(--text)',
            border: '1px solid var(--border)', cursor: 'pointer',
          }}
        >
          <Icon name="plus" size={14} /> Tap a keg
        </button>
      </div>
    );
  }

  const displayBeer = status === 'on' ? beer : kickedBeer;
  const assignment = status === 'on' ? tap.currentAssignment : tap.mostRecentKicked;

  return (
    <div
      onClick={status === 'on' ? onOpen : undefined}
      style={{
        background: 'var(--bg-elevated)', border: '1px solid var(--border)',
        borderRadius: 8, padding: 20,
        display: 'flex', flexDirection: 'column', gap: 14,
        cursor: status === 'on' ? 'pointer' : 'default',
        transition: 'border-color 0.15s',
        minHeight: 196,
      }}
      onMouseEnter={(e) => { if (status === 'on') e.currentTarget.style.borderColor = 'var(--text-muted)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div className="uppercase-label">Tap {tap.tapNumber}{tap.label ? ` · ${tap.label}` : ''}</div>
        <StatusPill status={status} />
      </div>

      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2,
          color: status === 'kicked' ? 'var(--text-muted)' : 'var(--text)',
          textDecoration: status === 'kicked' ? 'line-through' : 'none',
        }}>
          {displayBeer?.name ?? '—'}
        </div>
        <div style={{
          fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: 4,
          display: 'flex', gap: 10, flexWrap: 'wrap',
        }}>
          {displayBeer?.style && <span>{displayBeer.style}</span>}
          {displayBeer?.style && displayBeer?.abv != null && <span>·</span>}
          {displayBeer?.abv != null && <span className="mono">{displayBeer.abv}% ABV</span>}
        </div>
      </div>

      {assignment && (
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          fontSize: '0.75rem', color: 'var(--text-muted)',
        }}>
          <span>{assignment.kegSize ? (KEG_SIZE_LABEL[assignment.kegSize] ?? assignment.kegSize) : 'Unknown size'}</span>
          <span className="mono">Tapped {hoursAgo(assignment.tappedAt)}</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
        {status === 'kicked' ? (
          <button
            onClick={onSwap}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              fontWeight: 700, fontSize: '0.8125rem', padding: '6px 12px',
              borderRadius: 6, background: 'var(--text)', color: 'var(--bg)',
              border: '1px solid var(--text)', cursor: 'pointer',
            }}
          >
            <Icon name="swap" size={14} /> Swap keg
          </button>
        ) : (
          <>
            <button
              onClick={onOpen}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                fontWeight: 700, fontSize: '0.8125rem', padding: '6px 12px',
                borderRadius: 6, background: 'transparent', color: 'var(--text)',
                border: '1px solid transparent', cursor: 'pointer',
              }}
            >
              Details
            </button>
            <div style={{ flex: 1 }} />
            <button
              onClick={onKick}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                fontWeight: 700, fontSize: '0.8125rem', padding: '6px 12px',
                borderRadius: 6, background: 'transparent', color: 'var(--text)',
                border: '1px solid var(--border)', cursor: 'pointer',
              }}
            >
              Mark kicked
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Beer detail view
// ---------------------------------------------------------------------------

interface TapDetailProps {
  tap: Tap;
  beer: Beer | undefined;
  status: TapStatus;
  onBack: () => void;
  onKick: () => void;
  onSwap: () => void;
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 16,
      padding: '12px 0', borderBottom: '1px solid var(--border)',
    }}>
      <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{label}</div>
      <div style={{ fontWeight: 600, textAlign: 'right' }} className="mono">{value}</div>
    </div>
  );
}

function TapDetail({ tap, beer, status, onBack, onKick, onSwap }: TapDetailProps) {
  const assignment = tap.currentAssignment ?? tap.mostRecentKicked;

  return (
    <div style={{ padding: '24px 32px 64px', maxWidth: 1080, margin: '0 auto' }}>
      <button
        onClick={onBack}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          color: 'var(--text-muted)', fontSize: '0.875rem', fontWeight: 600,
          marginBottom: 24, background: 'none', border: 'none', cursor: 'pointer', padding: 0,
        }}
      >
        <Icon name="arrow-left" size={14} /> Back to tap list
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 48, alignItems: 'start' }}>
        {/* Left column */}
        <div>
          <div className="uppercase-label" style={{ marginBottom: 8 }}>
            Tap {tap.tapNumber}{tap.label ? ` · ${tap.label}` : ''} · {beer?.style ?? 'Unknown style'}
          </div>
          <h1 style={{
            margin: 0, fontSize: '2.5rem', fontWeight: 900,
            letterSpacing: '-0.02em', lineHeight: 1.05,
          }}>
            {beer?.name ?? '—'}
          </h1>

          {/* Specs row */}
          <div style={{
            display: 'flex', gap: 28, marginTop: 28, paddingTop: 24,
            borderTop: '1px solid var(--border)', flexWrap: 'wrap',
          }}>
            {beer?.abv != null && (
              <div>
                <div className="uppercase-label" style={{ marginBottom: 4 }}>ABV</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700 }} className="mono">{beer.abv}%</div>
              </div>
            )}
            {assignment?.kegSize && (
              <div>
                <div className="uppercase-label" style={{ marginBottom: 4 }}>Keg</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{KEG_SIZE_LABEL[assignment.kegSize] ?? assignment.kegSize}</div>
              </div>
            )}
          </div>

          {/* Tasting notes (description) */}
          {beer?.description && (
            <section style={{ marginTop: 36 }}>
              <h2 style={{ margin: '0 0 10px', fontSize: '1.25rem', fontWeight: 700 }}>
                What to tell guests
              </h2>
              <p style={{ margin: 0, fontSize: '1.0625rem', lineHeight: 1.55 }}>
                {beer.description}
              </p>
            </section>
          )}

          {/* Tags / dietary */}
          {beer && beer.tags.length > 0 && (
            <section style={{ marginTop: 32 }}>
              <h2 style={{ margin: '0 0 10px', fontSize: '1.25rem', fontWeight: 700 }}>Tags</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {beer.tags.map(t => (
                  <span key={t} style={{
                    padding: '6px 10px', fontSize: '0.8125rem', fontWeight: 600,
                    background: 'var(--bg-subtle)', borderRadius: 4,
                    border: '1px solid var(--border)',
                  }}>{t}</span>
                ))}
              </div>
            </section>
          )}

          {/* Assignment notes */}
          {assignment?.notes && (
            <section style={{ marginTop: 32 }}>
              <h2 style={{ margin: '0 0 8px', fontSize: '1.25rem', fontWeight: 700 }}>Tap notes</h2>
              <p style={{ margin: 0, color: 'var(--text-muted)' }}>{assignment.notes}</p>
            </section>
          )}
        </div>

        {/* Right column — keg status */}
        <aside style={{
          background: 'var(--bg-elevated)', border: '1px solid var(--border)',
          borderRadius: 8, padding: 24, position: 'sticky', top: 24,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Keg status</h3>
            <StatusPill status={status} />
          </div>

          {assignment && (
            <>
              <DetailRow label="Tapped" value={formatDayTime(assignment.tappedAt)} />
              {assignment.endedAt && <DetailRow label="Kicked" value={formatDayTime(assignment.endedAt)} />}
              <DetailRow label="On for" value={hoursAgo(assignment.tappedAt).replace(' ago', '')} />
              {assignment.kegSize && <DetailRow label="Size" value={KEG_SIZE_LABEL[assignment.kegSize] ?? assignment.kegSize} />}
            </>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 20 }}>
            {status === 'kicked' ? (
              <button
                onClick={onSwap}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  width: '100%', fontWeight: 700, fontSize: '0.9375rem', padding: '10px 16px',
                  borderRadius: 6, background: 'var(--text)', color: 'var(--bg)',
                  border: '1px solid var(--text)', cursor: 'pointer',
                }}
              >
                <Icon name="swap" size={16} /> Swap keg
              </button>
            ) : (
              <>
                <button
                  onClick={onKick}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    width: '100%', fontWeight: 700, fontSize: '0.9375rem', padding: '10px 16px',
                    borderRadius: 6, background: 'transparent', color: 'var(--text)',
                    border: '1px solid var(--border)', cursor: 'pointer',
                  }}
                >
                  Mark kicked
                </button>
                <button
                  onClick={onSwap}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    width: '100%', fontWeight: 700, fontSize: '0.9375rem', padding: '10px 16px',
                    borderRadius: 6, background: 'transparent', color: 'var(--text)',
                    border: 'none', cursor: 'pointer',
                  }}
                >
                  <Icon name="swap" size={16} /> Replace mid-keg
                </button>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function TapsPage(): React.ReactElement {
  const { locationId, locations, setLocationId } = useLocation();
  const { data, isLoading, isError } = useTaps(locationId);
  const { beerMap, isLoading: beersLoading } = useBeersMap();
  const kick = useKickTap(locationId);
  const { toast } = useToast();

  const [selectedTapId, setSelectedTapId] = useState<string | null>(null);
  const [swapTapId, setSwapTapId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'kicked'>('all');
  const [search, setSearch] = useState('');

  // History tab state
  const [view, setView] = useState<'taps' | 'history'>('taps');
  const [historyOffset, setHistoryOffset] = useState(0);
  const [historyItems, setHistoryItems] = useState<TapChangeLogEntry[]>([]);
  const HISTORY_PAGE_SIZE = 50;

  const { data: historyData, isFetching: historyFetching, isPlaceholderData: historyIsPlaceholder } = useTapChangeLog({
    ...(locationId != null ? { locationId } : {}),
    offset: historyOffset,
    limit: HISTORY_PAGE_SIZE,
  });

  // Accumulate history pages — bail early if React Query is serving placeholder data
  // to avoid overwriting already-loaded pages with stale page-1 data.
  useEffect(() => {
    if (!historyData || historyIsPlaceholder) return;
    if (historyData.offset === 0) {
      setHistoryItems(historyData.items);
    } else {
      setHistoryItems(prev => [...prev, ...historyData.items]);
    }
  }, [historyData, historyIsPlaceholder]);

  const taps = useMemo(
    () => (data?.items ?? []).sort((a, b) => a.tapNumber - b.tapNumber),
    [data],
  );

  const counts = useMemo(() => ({
    on:     taps.filter(t => tapStatus(t) === 'on').length,
    kicked: taps.filter(t => tapStatus(t) === 'kicked' || tapStatus(t) === 'empty').length,
  }), [taps]);

  const filtered = useMemo(() => {
    return taps.filter(t => {
      const st = tapStatus(t);
      if (filter === 'kicked') return st === 'kicked' || st === 'empty';
      if (search) {
        const q = search.toLowerCase();
        const beer = t.currentAssignment ? beerMap.get(t.currentAssignment.beerId) : undefined;
        return (beer?.name ?? '').toLowerCase().includes(q)
          || (beer?.style ?? '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [taps, filter, search, beerMap]);

  const handleKick = (tap: Tap) => {
    if (!tap.currentAssignment) return;
    const beer = beerMap.get(tap.currentAssignment.beerId);
    kick.mutate(
      { assignmentId: tap.currentAssignment.id, promoteReserved: !!tap.reservedAssignment },
      {
        onSuccess: () => toast.success(`${beer?.name ?? 'Tap'} marked kicked.`),
        onError: () => toast.error('Failed to mark kicked. Try again.'),
      },
    );
  };

  // --- detail view ---
  const selectedTap = selectedTapId ? taps.find(t => t.id === selectedTapId) : null;
  if (selectedTap) {
    const st = tapStatus(selectedTap);
    const beer = selectedTap.currentAssignment
      ? beerMap.get(selectedTap.currentAssignment.beerId)
      : selectedTap.mostRecentKicked
        ? beerMap.get(selectedTap.mostRecentKicked.beerId)
        : undefined;

    return (
      <>
        <TapDetail
          tap={selectedTap}
          beer={beer}
          status={st}
          onBack={() => setSelectedTapId(null)}
          onKick={() => {
            handleKick(selectedTap);
            setSelectedTapId(null);
          }}
          onSwap={() => {
            setSwapTapId(selectedTap.id);
            setSelectedTapId(null);
          }}
        />
        {swapTapId && (
          <SwapModalWrapper
            tapId={swapTapId}
            taps={taps}
            locationId={locationId!}
            onClose={() => setSwapTapId(null)}
            onAssigned={(name) => {
              toast.success(`Tap ${taps.find(t => t.id === swapTapId)?.tapNumber} → ${name}.`);
              setSwapTapId(null);
            }}
          />
        )}
      </>
    );
  }

  // --- list view ---
  return (
    <>
      <div style={{ padding: '24px 32px 64px', maxWidth: 1480, margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          gap: 24, marginBottom: 28, flexWrap: 'wrap',
        }}>
          <div>
            <div className="uppercase-label" style={{ marginBottom: 6 }}>
              {new Date().toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
            </div>
            <h1 style={{ margin: 0, fontSize: '2.25rem', fontWeight: 900, letterSpacing: '-0.02em' }}>
              Tap list
            </h1>
            <div style={{ color: 'var(--text-muted)', marginTop: 6, fontSize: '0.9375rem' }}>
              {isLoading || beersLoading
                ? 'Loading…'
                : `${counts.on} on tap · ${counts.kicked} need attention`}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* View toggle: Taps / History */}
            <div style={{
              display: 'flex', background: 'var(--bg-subtle)',
              border: '1px solid var(--border)', borderRadius: 6, padding: 2,
            }}>
              {([
                { k: 'taps', label: 'Taps' },
                { k: 'history', label: 'History' },
              ] as const).map(opt => (
                <button
                  key={opt.k}
                  onClick={() => {
                    if (opt.k === 'taps') {
                      setHistoryOffset(0);
                      setHistoryItems([]);
                    }
                    setView(opt.k);
                  }}
                  style={{
                    padding: '6px 12px', borderRadius: 4, fontSize: '0.8125rem', fontWeight: 700,
                    background: view === opt.k ? 'var(--bg)' : 'transparent',
                    color: view === opt.k ? 'var(--text)' : 'var(--text-muted)',
                    border: view === opt.k ? '1px solid var(--border)' : '1px solid transparent',
                    cursor: 'pointer',
                  }}
                >{opt.label}</button>
              ))}
            </div>

            {/* Search — only shown in taps view */}
            {view === 'taps' && (
              <div style={{ position: 'relative' }}>
                <div style={{
                  position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                  color: 'var(--text-subtle)', display: 'flex',
                }}>
                  <Icon name="search" size={16} />
                </div>
                <input
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setFilter('all'); }}
                  placeholder="Search beer or style"
                  style={{ paddingLeft: 36, width: 260 }}
                />
              </div>
            )}

            <LocationPills
              locations={locations}
              selectedId={locationId}
              onChange={(id) => {
                setLocationId(id);
                setHistoryOffset(0);
                setHistoryItems([]);
              }}
            />

            {/* Filter tabs — only shown in taps view */}
            {view === 'taps' && (
              <div style={{
                display: 'flex', background: 'var(--bg-subtle)',
                border: '1px solid var(--border)', borderRadius: 6, padding: 2,
              }}>
                {([
                  { k: 'all', label: 'All' },
                  { k: 'kicked', label: `Kicked (${counts.kicked})` },
                ] as const).map(opt => (
                  <button
                    key={opt.k}
                    onClick={() => { setFilter(opt.k); setSearch(''); }}
                    style={{
                      padding: '6px 12px', borderRadius: 4, fontSize: '0.8125rem', fontWeight: 700,
                      background: filter === opt.k ? 'var(--bg)' : 'transparent',
                      color: filter === opt.k ? 'var(--text)' : 'var(--text-muted)',
                      border: filter === opt.k ? '1px solid var(--border)' : '1px solid transparent',
                      cursor: 'pointer',
                    }}
                  >{opt.label}</button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Taps view */}
        {view === 'taps' && (
          <>
            {/* Error state */}
            {isError && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', marginBottom: 20,
                background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                borderLeft: '3px solid var(--text)', borderRadius: 6,
              }}>
                <Icon name="alert" size={18} />
                <div style={{ fontSize: '0.875rem' }}>
                  Could not load taps. Check your connection and try again.
                </div>
              </div>
            )}

            {/* Loading skeleton */}
            {(isLoading || beersLoading) && (
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14,
              }}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} style={{
                    background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                    borderRadius: 8, minHeight: 196,
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }} />
                ))}
              </div>
            )}

            {/* Tap grid */}
            {!isLoading && !beersLoading && (
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14,
              }}>
                {filtered.map(tap => {
                  const st = tapStatus(tap);
                  const beer = tap.currentAssignment ? beerMap.get(tap.currentAssignment.beerId) : undefined;
                  const kickedBeer = tap.mostRecentKicked ? beerMap.get(tap.mostRecentKicked.beerId) : undefined;
                  return (
                    <TapCard
                      key={tap.id}
                      tap={tap}
                      beer={beer}
                      kickedBeer={kickedBeer}
                      status={st}
                      onOpen={() => setSelectedTapId(tap.id)}
                      onKick={() => handleKick(tap)}
                      onSwap={() => setSwapTapId(tap.id)}
                    />
                  );
                })}
              </div>
            )}

            {!isLoading && !beersLoading && filtered.length === 0 && !isError && (
              <div style={{
                padding: 64, textAlign: 'center', color: 'var(--text-subtle)',
                border: '1px dashed var(--border)', borderRadius: 8,
              }}>
                {search ? `No taps match "${search}".` : 'No taps in this view.'}
              </div>
            )}
          </>
        )}

        {/* History view */}
        {view === 'history' && (
          <>
            <TapHistoryTable
              items={historyItems}
              showLocationColumn={false}
              isLoading={historyFetching && historyOffset === 0}
            />
            {historyItems.length < (historyData?.total ?? 0) && (
              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                <button
                  disabled={historyFetching}
                  onClick={() => setHistoryOffset(prev => prev + HISTORY_PAGE_SIZE)}
                >
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Swap modal */}
      {swapTapId && locationId && (
        <SwapModalWrapper
          tapId={swapTapId}
          taps={taps}
          locationId={locationId}
          onClose={() => setSwapTapId(null)}
          onAssigned={(name) => {
            const num = taps.find(t => t.id === swapTapId)?.tapNumber;
            toast.success(`Tap ${num} → ${name}.`);
            setSwapTapId(null);
          }}
        />
      )}
    </>
  );
}

